import React, { useMemo } from 'react'
import BedbankRoomOption from './BedbankRoomOption'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { sortBy } from 'lib/array/arrayUtils'
import useQueryParams from 'hooks/useQueryParams'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import uuidV4 from 'lib/string/uuidV4Utils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { GOOGLE_ADS_UTM_CONTENT } from 'constants/bedbank'
import { getSource } from 'selectors/offerSelectors'
import { useAppSelector } from 'hooks/reduxHooks'

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  offer: App.BedbankOffer;
  cheapestFlightPrice?: number;
  onUserAnalyticsAction?: (action: string, label?: string) => void;
  onViewPackageOptions: () => void;
  packageViews: Array<App.BedbankOfferPackageView>;
  fetchingRates?: boolean;
}

function BedbankRoomOptions(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    offer,
    onViewPackageOptions,
    packageViews,
    fetchingRates,
  } = props

  const query = useQueryParams()
  const validCheckinCheckoutDates = !!(checkInDate && checkOutDate)
  const roomTypeId = query.get('roomTypeId')
  const utmContent = query.get('utm_content')
  const source = useAppSelector((state) => getSource(state))

  const hasPromotions = offer.promotions.length > 0
  const showAll = packageViews.filter(p => p.available).length === 0

  const sortedPackageViews = useMemo(() => {
    return sortBy(packageViews, pkg => {
      if (pkg.cheapestRate) {
        return pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)
      } else if (hasPromotions) {
        return Number.MAX_SAFE_INTEGER
      }

      if (validCheckinCheckoutDates) {
        if (!pkg.capacityValid) {
          return Number.MAX_SAFE_INTEGER - 1
        }

        return Number.MAX_SAFE_INTEGER
      }

      if (pkg.capacityValid) {
        return Number.MIN_SAFE_INTEGER
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')
  }, [packageViews, validCheckinCheckoutDates, hasPromotions])

  const sortedPackages = useMemo(() => {
    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === roomTypeId)
    if (utmContent === GOOGLE_ADS_UTM_CONTENT && highlightedPackage) {
      return [
        highlightedPackage,
        sortedPackageViews.find(p => p.package.roomId !== roomTypeId),
      ].filter((p): p is App.BedbankOfferPackageView => !!p) // this fix type inference
    }
    else {
      return sortedPackageViews
    }
  }, [sortedPackageViews, utmContent, roomTypeId])

  const displayablePackages = useMemo(() => sortedPackages.filter(
    packageView => (packageView.show && packageView.available) || (packageView.show && showAll),
  ), [sortedPackages, showAll])

  const roomListId = useMemo(() => uuidV4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayablePackages]) // regenerate id if packages change

  return <VerticalSpacer gap={32}>
    {displayablePackages.map((packageView, index) => <BedbankRoomOption
      key={packageView.package.roomId}
      offer={offer}
      packageView={packageView}
      fetchingRates={fetchingRates}
      isHighlighted={utmContent === GOOGLE_ADS_UTM_CONTENT && roomTypeId === packageView.package.roomId}
      onViewPackageOptions={onViewPackageOptions}
      roomListId={roomListId}
      roomPosition={index + 1}
      source={source}
    />)}
  </VerticalSpacer>
}

type MappedProps = Pick<Props, 'checkInDate' | 'checkOutDate'>

export default withOfferPageAndReduxState((state: OfferPageState): MappedProps => {
  return {
    checkInDate: state.checkInDate,
    checkOutDate: getCheckoutDate(state),
  }
}, BedbankRoomOptions)
